<template>
    <DashboardSectionCard
        :title="t('vue.dashboard_overview_health_title')"
        :subtitle="t('vue.dashboard_overview_health_subtitle')"
    >
        <div
            class="grid grid-cols-1 items-center gap-[21px] sm:grid-cols-[132px_1fr]"
        >
            <DashboardDonut :segments="segments" :size="116">
                <span class="text-2xl font-semibold leading-none text-gray-900">
                    {{ health.healthyDomains }}
                </span>
                <span class="mt-1 text-[11px] leading-[14px] text-gray-500">
                    {{
                        t("vue.dashboard_overview_health_ring_label", {
                            total: health.monitoredDomains,
                        })
                    }}
                </span>
            </DashboardDonut>

            <div>
                <div class="mb-3 flex items-center gap-2">
                    <span
                        class="h-2 w-2 rounded-full"
                        :class="statusDotClass"
                    />
                    <span class="text-sm font-medium text-gray-900">
                        {{
                            t(
                                `vue.dashboard_overview_health_status_${health.status}`,
                            )
                        }}
                    </span>
                </div>

                <div
                    v-for="row in rows"
                    :key="row.label"
                    class="flex min-h-[34px] items-center justify-between border-t border-gray-200 text-[13px] leading-[18px]"
                >
                    <span class="text-gray-600">{{ row.label }}</span>
                    <span class="font-medium" :class="row.toneClass">
                        {{ row.value }}
                    </span>
                </div>
            </div>
        </div>
    </DashboardSectionCard>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { useTranslations } from "@/composables/useTranslations";
import DashboardSectionCard from "./DashboardSectionCard.vue";
import DashboardDonut, { type DonutSegment } from "./DashboardDonut.vue";
import type {
    IAccountHealth,
    IMonitoringServiceState,
} from "@/types/Dashboard/IDashboardOverview";

interface HealthRow {
    label: string;
    value: string;
    toneClass: string;
}

const props = defineProps<{
    health: IAccountHealth;
    blacklist: IMonitoringServiceState | null;
    blacklistLoading: boolean;
}>();

const { t } = useTranslations();

const GOOD_TONE = "text-green-600";
const WARN_TONE = "text-orange-600";
const NEUTRAL_TONE = "text-gray-400";

const HEALTHY_SEGMENT_COLOR = "#00c951";
const REMAINING_SEGMENT_COLOR = "#e5e7eb";

const segments = computed<DonutSegment[]>(() => {
    const healthy = props.health.healthyDomains;
    const remaining = Math.max(props.health.monitoredDomains - healthy, 0);

    return [
        { key: "healthy", value: healthy, color: HEALTHY_SEGMENT_COLOR },
        { key: "remaining", value: remaining, color: REMAINING_SEGMENT_COLOR },
    ];
});

const statusDotClass = computed<string>(() => {
    switch (props.health.status) {
        case "healthy":
            return "bg-green-500";
        case "critical":
            return "bg-red-500";
        default:
            return "bg-orange-500";
    }
});

const blacklistToneClass = computed<string>(() => {
    if (props.blacklistLoading === true || props.blacklist === null) {
        return NEUTRAL_TONE;
    }

    return (props.blacklist.count ?? 0) === 0 ? GOOD_TONE : WARN_TONE;
});

const resolveBlacklistValue = (): string => {
    if (props.blacklistLoading === true) {
        return t("vue.dashboard_overview_loading");
    }

    if (props.blacklist === null) {
        return t("vue.dashboard_overview_unavailable");
    }

    const issues = props.blacklist.count ?? 0;

    if (issues === 0) {
        return t("vue.dashboard_overview_blacklist_clean");
    }

    return t(
        `vue.dashboard_overview_blacklist_issues_${issues === 1 ? "one" : "other"}`,
        { count: issues },
    );
};

const rows = computed<HealthRow[]>(() => [
    {
        label: t("vue.dashboard_overview_health_dmarc_readiness"),
        value: `${props.health.dmarcReadinessPercentage}%`,
        toneClass:
            props.health.dmarcReadinessPercentage === 100
                ? GOOD_TONE
                : WARN_TONE,
    },
    {
        label: t("vue.dashboard_overview_health_report_coverage"),
        value: `${props.health.reportCoveragePercentage}%`,
        toneClass:
            props.health.reportCoveragePercentage === 100
                ? GOOD_TONE
                : WARN_TONE,
    },
    {
        label: t("vue.dashboard_overview_health_alerts"),
        value: props.health.alertsEnabled
            ? t("vue.dashboard_overview_state_enabled")
            : t("vue.dashboard_overview_state_disabled"),
        toneClass: props.health.alertsEnabled ? GOOD_TONE : WARN_TONE,
    },
    {
        label: t("vue.dashboard_overview_health_email_reporting"),
        value: props.health.emailReportingActive
            ? t("vue.dashboard_overview_state_active")
            : t("vue.dashboard_overview_state_inactive"),
        toneClass: props.health.emailReportingActive ? GOOD_TONE : WARN_TONE,
    },
    {
        label: t("vue.dashboard_overview_health_blacklist"),
        value: resolveBlacklistValue(),
        toneClass: blacklistToneClass.value,
    },
]);
</script>
